import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Snackbar
} from '@mui/material';
import {
  Build as BuildIcon,
  Cable as CableIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { reconstructFromBOMLink } from '../services/bomGeneratorService';
import { exportCableSVG, canGenerateSVG } from '../services/customerSvgExportService';
import { calculateCablePrice, formatPrice } from '../services/pricingService';
import { generateSKU } from '../services/skuService';
import { cableImageCaptureService } from '../services/cableImageCaptureService';
import {
  closeCartTab,
  navigateCartTab,
  openCartTabDuringGesture
} from '../utils/connectorDisplayUtils';

export default function BOMViewer() {
  const navigate = useNavigate();
  const { bomId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bomData, setBomData] = useState(null);
  const [config, setConfig] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [bigCommerceLoading, setBigCommerceLoading] = useState(false);
  const [bigCommerceMessage, setBigCommerceMessage] = useState('');
  const [bigCommerceSuccess, setBigCommerceSuccess] = useState(false);
  const [cartUrl, setCartUrl] = useState('');
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [productUrl, setProductUrl] = useState('');
  const [priceInfo, setPriceInfo] = useState({ totalCost: 0 });

  // Normalize config structure for pricing service
  const normalizeConfigForPricing = (config) => {
    if (!config) return config;
    
    const normalized = { ...config };
    
    // Handle connector structure - pricing service expects endA/endB directly
    if (config.connectors) {
      if (config.connectors.endA && !normalized.endA) {
        normalized.endA = config.connectors.endA;
      }
      if (config.connectors.endB && !normalized.endB) {
        normalized.endB = config.connectors.endB;
      }
    }
    
    // Ensure length is in inches for pricing service
    if (normalized.length && typeof normalized.length === 'number') {
      // If length is in feet, convert to inches
      if (normalized.length < 100) { // Likely in feet if less than 100
        normalized.length = Math.round(normalized.length * 12);
      }
    }
    
    // Handle finishing structure
    if (config.finishing && !normalized.finishes) {
      normalized.finishes = config.finishing;
    }
    
    console.log('BOMViewer: Config normalization:', {
      original: config,
      normalized: normalized
    });
    
    return normalized;
  };

  useEffect(() => {
    const loadBOM = async () => {
      try {
        const compressedData = searchParams.get('data');
        if (!compressedData) {
          throw new Error('No BOM data found in URL');
        }

        const result = reconstructFromBOMLink(bomId, compressedData);
        setBomData(result.bom);
        setConfig(result.config);
      } catch (error) {
        console.error('Error loading BOM:', error);
        setError('Failed to load BOM. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    loadBOM();
  }, [bomId, searchParams]);

  // Calculate price when config is loaded
  useEffect(() => {
    let isMounted = true;
    
    async function fetchPrice() {
      if (calculateCablePrice && config) {
        try {
          console.log('BOMViewer: Calculating price for config:', config);
          console.log('BOMViewer: Config structure check:', {
            type: config.type,
            length: config.length,
            style: config.style,
            endA: config.endA,
            endB: config.endB,
            connectors: config.connectors,
            hybridConnectors: config.hybridConnectors,
            wireGauge: config.wireGauge,
            finishing: config.finishing,
            finishes: config.finishes
          });
          
          // Normalize config structure for pricing service
          const normalizedConfig = normalizeConfigForPricing(config);
          console.log('BOMViewer: Normalized config for pricing:', normalizedConfig);
          
          const result = await calculateCablePrice(normalizedConfig);
          console.log('BOMViewer: Pricing result:', result);
          
          if (isMounted) setPriceInfo(result);
        } catch (error) {
          console.error('Error calculating price:', error);
        }
      }
    }
    fetchPrice();
    return () => { isMounted = false; };
  }, [config]);

  const handleExportSVG = async () => {
    if (!canGenerateSVG(config)) {
      return;
    }

    setExporting(true);
    try {
      await exportCableSVG(config);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const handlePrintBOM = () => {
    if (!bomData) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill of Materials - ${bomData.name}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap" />
          <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
          <style>
            * { box-sizing: border-box; }
            body { 
              margin: 0; 
              padding: 0; 
              font-family: 'Roboto', Arial, sans-serif;
              background: white;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div id="printable-bom"></div>
          <script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
          <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
          <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
          <script type="text/babel">
            // Simple print component for the BOM
            const bomData = ${JSON.stringify(bomData)};
            
            function PrintBOM() {
              const formatDate = (timestamp) => {
                return new Date(timestamp).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              };
              
              return (
                <div style={{
                  width: '100%',
                  maxWidth: '800px',
                  margin: '0 auto',
                  padding: '20px',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '20px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    borderBottom: '3px solid #1976d2',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                  }}>
                    <h1 style={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      marginBottom: '10px',
                      fontSize: '28px'
                    }}>
                      Bill of Materials
                    </h1>
                    <h2 style={{
                      color: '#34495e',
                      marginBottom: '15px',
                      fontSize: '20px'
                    }}>
                      {bomData.name}
                    </h2>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                      <div><strong>SKU:</strong> <span style={{ fontFamily: 'monospace' }}>{bomData.sku}</span></div>
                      <div><strong>BOM ID:</strong> <span style={{ fontFamily: 'monospace' }}>{bomData.id}</span></div>
                      <div><strong>Generated:</strong> {formatDate(bomData.timestamp)}</div>
                    </div>
                  </div>

                  {/* Technical Specifications */}
                  <div style={{
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                  }}>
                    <h3 style={{
                      color: '#1976d2',
                      fontWeight: 600,
                      marginBottom: '15px',
                      fontSize: '18px',
                      borderBottom: '1px solid #e0e0e0',
                      paddingBottom: '5px'
                    }}>
                      Technical Specifications
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Cable Type:</span>
                        <span>{bomData.specifications.cableType}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Category:</span>
                        <span>{bomData.specifications.cableCategory}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Style:</span>
                        <span>{bomData.specifications.cableStyle}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Length:</span>
                        <span>{bomData.specifications.length.value} {bomData.specifications.length.unit}</span>
                      </div>
                      ${bomData.specifications.wireGauge ? `
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Wire Gauge:</span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>{bomData.specifications.wireGauge.gauge} AWG</span>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '${bomData.specifications.wireGauge.color?.toLowerCase() || '#000'}',
                            border: '1px solid #ccc'
                          }}></div>
                        </div>
                      </div>
                      ` : ''}
                      ${bomData.specifications.wireType ? `
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Wire Type:</span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>{bomData.specifications.wireType}</span>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#4a5568',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>W</div>
                        </div>
                      </div>
                      ` : ''}
                    </div>
                  </div>

                  {/* Components */}
                  <div style={{
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                  }}>
                    <h3 style={{
                      color: '#1976d2',
                      fontWeight: 600,
                      marginBottom: '15px',
                      fontSize: '18px',
                      borderBottom: '1px solid #e0e0e0',
                      paddingBottom: '5px'
                    }}>
                      Components (${bomData.components.length})
                    </h3>
                    <div>
                      ${bomData.components.map(component => `
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          marginBottom: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          backgroundColor: '#fafafa'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>${component.description}</div>
                            <div style={{ color: '#666', fontSize: '14px' }}>${component.specification}</div>
                          </div>
                          <div style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            minWidth: '60px',
                            textAlign: 'center'
                          }}>
                            Qty: ${component.quantity}
                          </div>
                        </div>
                      `).join('')}
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '2px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      Generated by NLFX Cable Builder | Professional Cable Solutions
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      This document contains detailed specifications for cable assembly and should be used for manufacturing and quality control purposes.
                    </div>
                  </div>
                </div>
              );
            }
            
            ReactDOM.render(<PrintBOM />, document.getElementById('printable-bom'));
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Handle BigCommerce integration
  const handleAddToBigCommerce = async () => {
    console.log('=== BOM ADD TO CART BUTTON CLICKED ===');
    const cartWindow = openCartTabDuringGesture();
    setBigCommerceLoading(true);
    setBigCommerceMessage('');
    setBigCommerceSuccess(false);
    setCartUrl('');
    setProductUrl('');
    setPopupBlocked(false);
    
    try {
      // Ensure price is calculated
      let finalPrice = priceInfo?.totalCost || 0;
      
      if (!finalPrice && config && calculateCablePrice) {
        console.log('Calculating price for BigCommerce...');
        const recalculatedPrice = await calculateCablePrice(config);
        finalPrice = recalculatedPrice?.totalCost || 0;
      }

      if (!finalPrice) {
        throw new Error('Unable to calculate price for this configuration');
      }

      // Generate SKU for this configuration
      const sku = generateSKU(config);
      
      // For BOM viewer, we don't have direct access to SVG elements
      // The image will be captured when the user builds the cable in the main builder
      console.log('BOM viewer - image capture will happen during cable building process');
      
      // Create configuration with SKU for BigCommerce
      const configWithSku = {
        ...config,
        sku
      };
      
      // Call the BigCommerce API endpoint
      const response = await fetch('/api/bigcommerce-cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          config: configWithSku,
          price: finalPrice
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('=== API RESPONSE ===', result);
      
      if (result.success) {
        console.log('=== SUCCESS: Product created successfully ===');
        console.log('Product ID:', result.result.product_id);
        console.log('Cart URL:', result.result.cart_url);
        console.log('Redirect URL:', result.result.redirect_url);
        
        setBigCommerceMessage(`Product created! SKU: ${result.result.sku}`);
        setBigCommerceSuccess(true);
        setCartUrl(result.result.cart_url || 'https://www.nlfxpro.com/cart.php');
        
        // Generate product URL with correct custom cable format
        const productPageUrl = `https://www.nlfxpro.com/custom-cable-${result.result.sku.toLowerCase()}?showHidden=true`;
        setProductUrl(productPageUrl);
        
        // Navigate pre-opened cart tab (opened on click to avoid popup blockers)
        const redirectUrl = result.result.redirect_url || result.result.cart_url;
        console.log('=== AUTO-OPENING CART ===', redirectUrl);

        if (redirectUrl) {
          const navigated = navigateCartTab(cartWindow, redirectUrl);
          if (navigated) {
            setPopupBlocked(false);
            setBigCommerceMessage(
              `Product created and added to cart! SKU: ${result.result.sku}`
            );
          } else {
            console.log('Popup blocked - showing fallback message');
            setPopupBlocked(true);
            setBigCommerceMessage(
              `Product created! Popup blocked - use "Add to Cart" button to add item.`
            );
          }
        }
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
      
    } catch (error) {
      console.error('BigCommerce integration error:', error);
      setBigCommerceMessage(`Failed to add to cart: ${error.message}`);
      setBigCommerceSuccess(false);
      closeCartTab(cartWindow);
    } finally {
      setBigCommerceLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.open('https://www.nlfxpro.com/nlfx-cable-builder/', '_blank')}
        >
          Go to Cable Builder
        </Button>
      </Container>
    );
  }

  if (!bomData || !config) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          BOM data is incomplete or corrupted.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4" component="h1">
          Bill of Materials
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* BOM Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {bomData.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              SKU: {bomData.sku}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(bomData.timestamp).toLocaleString()}
            </Typography>
          </Paper>
        </Grid>

        {/* Cable Diagram */}
        {bomData.svgContent && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Cable Diagram
              </Typography>
              <Box 
                sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  p: 2,
                  bgcolor: '#fafafa'
                }}
                dangerouslySetInnerHTML={{ __html: bomData.svgContent }}
              />
              {canGenerateSVG(config) && (
                <Button
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleExportSVG}
                  disabled={exporting}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  {exporting ? 'Exporting...' : 'Download SVG'}
                </Button>
              )}
            </Paper>
          </Grid>
        )}

        {/* Components List */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Components
            </Typography>
            <List dense>
              {bomData.components.map((component, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    {component.type === 'cable' && <CableIcon />}
                    {component.type === 'connector' && <SettingsIcon />}
                    {component.type === 'wire' && <CableIcon />}
                    {component.type === 'finishing' && <BuildIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={component.description}
                    secondary={`${component.specification} - Qty: ${component.quantity}`}
                  />
                  <Chip 
                    label={component.type} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Specifications */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Specifications
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Cable Type
                </Typography>
                <Typography variant="body1">
                  {bomData.specifications.cableType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body1">
                  {bomData.specifications.cableCategory}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Style
                </Typography>
                <Typography variant="body1">
                  {bomData.specifications.cableStyle}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Length
                </Typography>
                <Typography variant="body1">
                  {bomData.specifications.length.value} {bomData.specifications.length.unit}
                </Typography>
              </Grid>
              {bomData.specifications.wireGauge && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Wire Gauge
                  </Typography>
                  <Typography variant="body1">
                    {bomData.specifications.wireGauge.gauge} AWG {bomData.specifications.wireGauge.color}
                  </Typography>
                </Grid>
              )}
              {bomData.specifications.wireType && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Wire Type
                  </Typography>
                  <Typography variant="body1">
                    {bomData.specifications.wireType}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={bigCommerceLoading ? <CircularProgress size={20} /> : <AddIcon />}
                onClick={handleAddToBigCommerce}
                sx={{ flex: 1 }}
                disabled={bigCommerceLoading || !priceInfo || typeof priceInfo.totalCost !== 'number'}
                title="Add to Cart"
              >
                {bigCommerceLoading ? (
                  'Adding to Cart...'
                ) : priceInfo && typeof priceInfo.totalCost === 'number' ? (
                  <>Add to Cart – {formatPrice(priceInfo.totalCost)}</>
                ) : (
                  'Price unavailable'
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.open('https://www.nlfxpro.com/nlfx-cable-builder/', '_blank')}
                size="large"
              >
                Start New Build
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintBOM}
                size="large"
              >
                Print BOM
              </Button>
            </Box>
            
            {/* View Product button - persistent until reset/new product */}
            {productUrl && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    console.log('BOMViewer: Opening product URL:', productUrl);
                    window.open(productUrl, '_blank');
                  }}
                  title="View Custom Cable Product Page"
                  sx={{ 
                    minWidth: 200,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  View Product Page
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar
        open={!!bigCommerceMessage}
        onClose={() => {
          setBigCommerceMessage('');
          setCartUrl('');
          setPopupBlocked(false);
          // Don't clear productUrl - it should persist until reset
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => {
            setBigCommerceMessage('');
            setCartUrl('');
            setPopupBlocked(false);
            // Don't clear productUrl - it should persist until reset
          }}
          severity={bigCommerceSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
          action={bigCommerceSuccess && cartUrl && popupBlocked ? (
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                window.open(cartUrl, '_blank');
              }}
              variant="outlined"
              sx={{ 
                color: 'inherit', 
                borderColor: 'currentColor',
                '&:hover': { 
                  borderColor: 'currentColor',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Add to Cart
            </Button>
          ) : null}
        >
          {bigCommerceMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
} 